import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import { cn } from '@/utils/cn';
import { useWorkPointRules } from '@/hooks/useWorkPointRules';
import { useAllReferees } from '@/hooks/useReferees';
import {
  createRule,
  updateRule,
  estimatePoints,
  getMatchDurations,
  updateMatchDurations,
} from '@/api/TournamentManagement/workPointApi';
import type {
  CreateWorkPointRuleInput,
  UpdateWorkPointRuleInput,
  WorkPointEstimateQuery,
  WorkPointEstimateView,
  UpdateMatchFormatDurationsInput,
} from '@/models/tournament';
import {
  MatchRole,
  MatchFormat,
  MatchType,
  DEFAULT_MATCH_FORMAT_DURATIONS,
  MATCH_DURATION_MIN_MINUTES,
  MATCH_DURATION_MAX_MINUTES,
} from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';
import { formatLocalDateParam } from '@/utils/apiDate';
import { appDate } from '@/utils/appDateFormat';
import { DateField, TimeField } from '@/components/dateFields';

const defaultFormValues: CreateWorkPointRuleInput = {
  headRefereeBasePoints: 100,
  assistantRefereeBasePoints: 60,
  fourthOfficialBasePoints: 30,
  commissionerBasePoints: 100,
  fiveAsidePoints: 20,
  sevenAsidePoints: 40,
  nineAsidePoints: 60,
  elevenAsidePoints: 80,
  indoorBonus: 10,
  outdoorBonus: 0,
  weekendHolidayBonus: 15,
  nightMatchBonus: 10,
  nightMatchThresholdHour: 20,
  earlyMorningBonus: 5,
  earlyMorningThresholdHour: 8,
  highRatingThreshold: 4.7,
  highRatingBonus: 15,
  midRatingThreshold: 4.0,
  midRatingBonus: 5,
  minPointsPerMatch: 10,
  maxPointsPerMatch: 300,
  pointToCurrencyRate: 1.0,
  // NEVER toISOString() — east of UTC it yields yesterday, back-dating a brand-new rule.
  effectiveFrom: formatLocalDateParam(new Date()),
  effectiveTo: undefined,
};

const inputClasses =
  'w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors outline-none';

const labelClasses = 'block text-sm font-medium text-foreground mb-1';

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  testId: string;
  step?: string;
  min?: number;
}

/* This one template renders 22 times, and its <label> had no htmlFor while its <input> had no id —
   so ~22 numeric fields on the page that sets referee PAY RATES reached a screen reader as unnamed
   spin buttons. ui/Input already wires id/htmlFor/aria-invalid/aria-describedby, and the testId is
   unique per field so it doubles as a stable DOM id. */
const NumberField: React.FC<NumberFieldProps> = ({ label, value, onChange, testId, step = '1', min = 0 }) => (
  <Input
    id={testId}
    type="number"
    label={label}
    value={value}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(Number(e.target.value))}
    step={step}
    min={min}
    data-testid={testId}
  />
);

const WorkPointSettings: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { code: currencyCode, format: formatCurrency } = useCurrency();
  const { data: rules, loading, error, refetch } = useWorkPointRules();
  const [form, setForm] = useState<CreateWorkPointRuleInput>(defaultFormValues);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // handleSave calls refetch(), which flips `loading` true again. The page used to early-return a
  // LoadingState for the WHOLE eight-section form on that flag, so pressing Save blanked every
  // section, threw away scroll position, and the "Rule saved successfully" chip only appeared once
  // everything remounted. Spinner on first load only.
  const isInitialLoad = loading && !rules;
  const isRefetching = loading && !!rules;

  // Preview calculator state
  const [previewRole, setPreviewRole] = useState<MatchRole | ''>('');
  const [previewFormat, setPreviewFormat] = useState<MatchFormat | ''>('');
  const [previewType, setPreviewType] = useState<MatchType | ''>('');
  // Explicit date+time so the server can determine weekend/holiday, night, early-morning
  const nowForDefault = new Date();
  const [previewDate, setPreviewDate] = useState<string>(formatLocalDateParam(nowForDefault));
  const [previewHour, setPreviewHour] = useState<string>('14:00');
  const [previewRefereeProfileId, setPreviewRefereeProfileId] = useState<string>('');
  const [previewResult, setPreviewResult] = useState<WorkPointEstimateView | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Referees list for the "assume this referee" dropdown — so performance bonus shows up
  const { data: refereesList } = useAllReferees();

  // Match Durations — a separate company setting with its own Save (owner decision 2026-09-17).
  // It is independent of the work-point rule: saving it never touches the rule, and it only applies to
  // matches created or edited afterwards.
  const [durations, setDurations] = useState<UpdateMatchFormatDurationsInput>({
    fiveAsideMinutes: DEFAULT_MATCH_FORMAT_DURATIONS.fiveAsideMinutes,
    sevenAsideMinutes: DEFAULT_MATCH_FORMAT_DURATIONS.sevenAsideMinutes,
    nineAsideMinutes: DEFAULT_MATCH_FORMAT_DURATIONS.nineAsideMinutes,
    elevenAsideMinutes: DEFAULT_MATCH_FORMAT_DURATIONS.elevenAsideMinutes,
  });
  const [durationsLoadError, setDurationsLoadError] = useState<string | null>(null);
  const [durationsSaving, setDurationsSaving] = useState(false);
  const [durationsError, setDurationsError] = useState<string | null>(null);
  const [durationsSuccess, setDurationsSuccess] = useState(false);

  const loadDurations = useCallback(async () => {
    setDurationsLoadError(null);
    try {
      const result = await getMatchDurations();
      if (result.errors.length === 0 && result.payload) {
        const d = result.payload;
        setDurations({
          fiveAsideMinutes: d.fiveAsideMinutes,
          sevenAsideMinutes: d.sevenAsideMinutes,
          nineAsideMinutes: d.nineAsideMinutes,
          elevenAsideMinutes: d.elevenAsideMinutes,
        });
      } else {
        setDurationsLoadError(result.errors[0]?.description || t('Failed to load match durations'));
      }
    } catch {
      setDurationsLoadError(t('Failed to load match durations'));
    }
  }, [t]);

  useEffect(() => {
    loadDurations();
  }, [loadDurations]);

  const updateDuration = (key: keyof UpdateMatchFormatDurationsInput, value: number) => {
    setDurations((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveDurations = async () => {
    setDurationsError(null);
    setDurationsSuccess(false);
    const values = Object.values(durations);
    if (values.some((v) => !Number.isInteger(v) || v < MATCH_DURATION_MIN_MINUTES || v > MATCH_DURATION_MAX_MINUTES)) {
      setDurationsError(t('Each duration must be a whole number between {{min}} and {{max}} minutes', {
        min: MATCH_DURATION_MIN_MINUTES,
        max: MATCH_DURATION_MAX_MINUTES,
      }));
      return;
    }
    setDurationsSaving(true);
    try {
      const result = await updateMatchDurations(durations);
      if (result.status < 400 && result.errors.length === 0) {
        setDurationsSuccess(true);
        setTimeout(() => setDurationsSuccess(false), 3000);
      } else {
        setDurationsError(result.errors[0]?.description || t('Failed to save match durations'));
      }
    } catch (error: any) {
      setDurationsError(error?.response?.data?.errors?.[0]?.description || t('Failed to save match durations'));
    } finally {
      setDurationsSaving(false);
    }
  };

  const activeRule = useMemo(() => {
    if (!rules || rules.length === 0) return null;
    return rules.find((r) => r.isActive) ?? rules[0];
  }, [rules]);

  useEffect(() => {
    if (activeRule) {
      setForm({
        headRefereeBasePoints: activeRule.headRefereeBasePoints,
        assistantRefereeBasePoints: activeRule.assistantRefereeBasePoints,
        fourthOfficialBasePoints: activeRule.fourthOfficialBasePoints,
        commissionerBasePoints: activeRule.commissionerBasePoints,
        fiveAsidePoints: activeRule.fiveAsidePoints,
        sevenAsidePoints: activeRule.sevenAsidePoints,
        nineAsidePoints: activeRule.nineAsidePoints,
        elevenAsidePoints: activeRule.elevenAsidePoints,
        indoorBonus: activeRule.indoorBonus,
        outdoorBonus: activeRule.outdoorBonus,
        weekendHolidayBonus: activeRule.weekendHolidayBonus,
        nightMatchBonus: activeRule.nightMatchBonus,
        nightMatchThresholdHour: activeRule.nightMatchThresholdHour,
        earlyMorningBonus: activeRule.earlyMorningBonus,
        earlyMorningThresholdHour: activeRule.earlyMorningThresholdHour,
        highRatingThreshold: activeRule.highRatingThreshold,
        highRatingBonus: activeRule.highRatingBonus,
        midRatingThreshold: activeRule.midRatingThreshold,
        midRatingBonus: activeRule.midRatingBonus,
        minPointsPerMatch: activeRule.minPointsPerMatch,
        maxPointsPerMatch: activeRule.maxPointsPerMatch,
        pointToCurrencyRate: activeRule.pointToCurrencyRate,
        effectiveFrom: activeRule.effectiveFrom?.split('T')[0] ?? '',
        effectiveTo: activeRule.effectiveTo?.split('T')[0],
      });
    }
  }, [activeRule]);

  const updateField = <K extends keyof CreateWorkPointRuleInput>(key: K, value: CreateWorkPointRuleInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    // A native date box typed in the wrong machine format used to leave effectiveFrom EMPTY; the
    // server then rejected the body at model binding with a 400 whose shape the error handler below
    // could not read, so the page showed a raw "Request failed with status code 400".
    // DateField only reports a value once the text is a real dd-mm-yyyy day, so an empty model covers
    // both "left blank" and "typed something impossible like 31-02-2026" — one message fits both.
    if (!form.effectiveFrom) {
      setSaveError(t('Enter a valid Effective From date (dd-mm-yyyy)'));
      return;
    }
    if (form.effectiveTo && form.effectiveTo < form.effectiveFrom) {
      setSaveError(t('Effective To must be on or after Effective From'));
      return;
    }
    setSaving(true);
    try {
      let result;
      if (activeRule) {
        const input: UpdateWorkPointRuleInput = { ...form, id: activeRule.id };
        result = await updateRule(input);
      } else {
        result = await createRule(form);
      }
      if (result.status < 400 && result.errors.length === 0) {
        setSaveSuccess(true);
        refetch();
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.errors[0]?.description || t('Failed to save rule'));
      }
    } catch (error: any) {
      const data = error?.response?.data;
      // ASP.NET model-binding failures return `errors` as an OBJECT of field → messages, not our array.
      const bindingErrors = data?.errors && !Array.isArray(data.errors)
        ? Object.values(data.errors as Record<string, string[]>).flat().filter(Boolean)
        : [];
      const msg = data?.errors?.[0]?.description
        || (bindingErrors.length > 0 ? t('Some values could not be read. Check the dates and numbers, then save again.') : null)
        || error?.message
        || t('Something went wrong. Please try again.');
      setSaveError(msg);
      console.error('Error saving work point rule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = useCallback(async () => {
    if (previewRole === '' || previewFormat === '' || previewType === '' || !previewDate || !previewHour) {
      setPreviewResult(null);
      return;
    }
    setPreviewLoading(true);
    try {
      // Send as local-wall-clock (no UTC shift). Night/early-morning thresholds are
      // compared against the HOUR the user picked locally — converting to UTC via
      // toISOString() would send a different hour and break the bonus calculation
      // for any viewer outside UTC (e.g. 20:00 local in UTC+3 becomes 17:00Z and
      // falls below the night threshold).
      const [h, m] = previewHour.split(':').map((s) => parseInt(s, 10));
      const hh = String(isNaN(h) ? 14 : h).padStart(2, '0');
      const mm = String(isNaN(m) ? 0 : m).padStart(2, '0');
      const localIso = `${previewDate}T${hh}:${mm}:00`;

      const query: WorkPointEstimateQuery = {
        role: previewRole as MatchRole,
        format: previewFormat as MatchFormat,
        type: previewType as MatchType,
        scheduledDateTime: localIso,
        refereeProfileId: previewRefereeProfileId ? parseInt(previewRefereeProfileId, 10) : undefined,
      };
      const result = await estimatePoints(query);
      if (result.status < 400 && result.errors.length === 0) {
        setPreviewResult(result.payload as WorkPointEstimateView);
      }
    } catch {
      // silently fail
    } finally {
      setPreviewLoading(false);
    }
  }, [previewRole, previewFormat, previewType, previewDate, previewHour, previewRefereeProfileId]);

  useEffect(() => {
    if (previewRole !== '' && previewFormat !== '' && previewType !== '' && previewDate && previewHour) {
      handlePreview();
    }
  }, [handlePreview, previewRole, previewFormat, previewType, previewDate, previewHour, previewRefereeProfileId]);

  return (
    <div data-testid="work-point-settings-page">
      <PageLayout
        title={t('Work Point Settings')}
        description={t('Configure work point calculation rules for referee compensation')}
        icon={Calculator}
        accentColor="amber"
        maxWidth="5xl"
      >
        {error && (
          <div
            role="alert"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3"
            data-testid="work-point-rules-error"
          >
            <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            <Button variant="outline" size="sm" onClick={refetch} className="ms-auto" data-testid="work-point-rules-retry-btn">
              {t('Try Again')}
            </Button>
          </div>
        )}

        {isInitialLoad ? (
          <div className="py-12">
            <LoadingState title={t('Loading data...')} />
          </div>
        ) : (
          <div
            className={cn(
              'space-y-6',
              'transition-opacity duration-200 ease-out motion-reduce:transition-none',
              isRefetching && 'opacity-60 pointer-events-none',
            )}
            aria-busy={isRefetching}
          >

          {/* Active Rule Status */}
          <div className="bg-card border border-border rounded-lg p-4" data-testid="active-rule-status">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={activeRule ? 'CheckCircle' : 'AlertCircle'} size={18} className={activeRule ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'} />
              <h2 className="text-lg font-semibold text-foreground">{t('Active Rule')}</h2>
            </div>
            {activeRule ? (
              <p className="text-sm text-muted-foreground">
                ID: {activeRule.id} | {t('Effective From')}: {appDate(activeRule.effectiveFrom)}
                {activeRule.effectiveTo && ` | ${t('Effective To')}: ${appDate(activeRule.effectiveTo)}`}
              </p>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-2">
                <p className="text-sm text-amber-800 dark:text-amber-300">{t('No work point calculation rule has been configured yet. Create one below.')}</p>
              </div>
            )}
          </div>

          {/* Base Points by Role */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-base-points">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Users" size={18} className="me-2 text-primary" />
              {t('Base Points by Role')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <NumberField
                label={t('Head Referee Base')}
                value={form.headRefereeBasePoints}
                onChange={(v) => updateField('headRefereeBasePoints', v)}
                testId="input-head-referee-base"
              />
              <NumberField
                label={t('Assistant Referee Base')}
                value={form.assistantRefereeBasePoints}
                onChange={(v) => updateField('assistantRefereeBasePoints', v)}
                testId="input-assistant-referee-base"
              />
              <NumberField
                label={t('Fourth Official Base')}
                value={form.fourthOfficialBasePoints}
                onChange={(v) => updateField('fourthOfficialBasePoints', v)}
                testId="input-fourth-official-base"
              />
              <NumberField
                label={t('Commissioner Base')}
                value={form.commissionerBasePoints}
                onChange={(v) => updateField('commissionerBasePoints', v)}
                testId="input-commissioner-base"
              />
            </div>
          </section>

          {/* Points by Match Format */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-format-points">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="LayoutGrid" size={18} className="me-2 text-primary" />
              {t('Points by Match Format')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <NumberField
                label={t('5-aside Points')}
                value={form.fiveAsidePoints}
                onChange={(v) => updateField('fiveAsidePoints', v)}
                testId="input-five-aside"
              />
              <NumberField
                label={t('7-aside Points')}
                value={form.sevenAsidePoints}
                onChange={(v) => updateField('sevenAsidePoints', v)}
                testId="input-seven-aside"
              />
              <NumberField
                label={t('9-aside Points')}
                value={form.nineAsidePoints}
                onChange={(v) => updateField('nineAsidePoints', v)}
                testId="input-nine-aside"
              />
              <NumberField
                label={t('11-aside Points')}
                value={form.elevenAsidePoints}
                onChange={(v) => updateField('elevenAsidePoints', v)}
                testId="input-eleven-aside"
              />
            </div>
          </section>

          {/* Match Type Bonuses */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-type-bonuses">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="MapPin" size={18} className="me-2 text-primary" />
              {t('Match Type Bonuses')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label={t('Indoor Bonus')}
                value={form.indoorBonus}
                onChange={(v) => updateField('indoorBonus', v)}
                testId="input-indoor-bonus"
              />
              <NumberField
                label={t('Outdoor Bonus')}
                value={form.outdoorBonus}
                onChange={(v) => updateField('outdoorBonus', v)}
                testId="input-outdoor-bonus"
              />
            </div>
          </section>

          {/* Time-Based Bonuses */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-time-bonuses">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Clock" size={18} className="me-2 text-primary" />
              {t('Time-Based Bonuses')}
            </h3>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4 text-xs text-blue-900 dark:text-blue-100" data-testid="time-bonus-help">
              <p className="font-medium mb-1 flex items-center"><Icon name="Info" size={13} className="me-1.5" />{t('How time-based bonuses are applied')}</p>
              <ul className="list-disc list-inside space-y-1 ms-1">
                <li>{t('Weekend/Holiday — applied when the match falls on a company holiday or a day with no scheduled shifts')}</li>
                <li>{t('Night match — applied when the match starts at or after the Night Threshold Hour (e.g. threshold of 20 means 8 PM and later)')}</li>
                <li>{t('Early morning — applied when the match starts strictly before the Early Morning Threshold Hour (e.g. threshold of 8 means any time before 8 AM)')}</li>
                <li>{t('All three can stack. Hours are interpreted as the local wall-clock time the match was scheduled for (not UTC)')}</li>
              </ul>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NumberField
                label={t('Weekend/Holiday Bonus')}
                value={form.weekendHolidayBonus}
                onChange={(v) => updateField('weekendHolidayBonus', v)}
                testId="input-weekend-holiday-bonus"
              />
              <NumberField
                label={t('Night Match Bonus')}
                value={form.nightMatchBonus}
                onChange={(v) => updateField('nightMatchBonus', v)}
                testId="input-night-match-bonus"
              />
              <NumberField
                label={t('Night Match Threshold Hour')}
                value={form.nightMatchThresholdHour}
                onChange={(v) => updateField('nightMatchThresholdHour', v)}
                testId="input-night-threshold"
                min={0}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <NumberField
                label={t('Early Morning Bonus')}
                value={form.earlyMorningBonus}
                onChange={(v) => updateField('earlyMorningBonus', v)}
                testId="input-early-morning-bonus"
              />
              <NumberField
                label={t('Early Morning Threshold Hour')}
                value={form.earlyMorningThresholdHour}
                onChange={(v) => updateField('earlyMorningThresholdHour', v)}
                testId="input-early-morning-threshold"
                min={0}
              />
            </div>
          </section>

          {/* Performance Bonuses */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-performance-bonuses">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="TrendingUp" size={18} className="me-2 text-primary" />
              {t('Performance Bonuses')}
            </h3>
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md p-3 mb-4 text-xs text-purple-900 dark:text-purple-100" data-testid="performance-bonus-help">
              <p className="font-medium mb-1 flex items-center"><Icon name="Info" size={13} className="me-1.5" />{t('How performance bonuses are applied')}</p>
              <ul className="list-disc list-inside space-y-1 ms-1">
                <li>{t('Each referee has a rolling performance rating (0–5) maintained on their profile, updated whenever HR rates them after a completed match.')}</li>
                <li>{t('When the work points are calculated, only ONE tier applies — the highest one the rating qualifies for')}</li>
                <li className="ms-4">{t('• If rating ≥ High Rating Threshold → add High Rating Bonus.')}</li>
                <li className="ms-4">{t('• Else if rating ≥ Mid Rating Threshold → add Mid Rating Bonus.')}</li>
                <li className="ms-4">{t('• Otherwise → no performance bonus.')}</li>
                <li>{t('The bonuses are additive with the base, format, type, and time bonuses, then the total is clamped between the Min and Max point caps.')}</li>
              </ul>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <NumberField
                label={t('High Rating Threshold')}
                value={form.highRatingThreshold}
                onChange={(v) => updateField('highRatingThreshold', v)}
                testId="input-high-rating-threshold"
                step="0.1"
              />
              <NumberField
                label={t('High Rating Bonus')}
                value={form.highRatingBonus}
                onChange={(v) => updateField('highRatingBonus', v)}
                testId="input-high-rating-bonus"
              />
              <NumberField
                label={t('Mid Rating Threshold')}
                value={form.midRatingThreshold}
                onChange={(v) => updateField('midRatingThreshold', v)}
                testId="input-mid-rating-threshold"
                step="0.1"
              />
              <NumberField
                label={t('Mid Rating Bonus')}
                value={form.midRatingBonus}
                onChange={(v) => updateField('midRatingBonus', v)}
                testId="input-mid-rating-bonus"
              />
            </div>
          </section>

          {/* Point Caps */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-point-caps">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Shield" size={18} className="me-2 text-primary" />
              {t('Point Caps')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label={t('Min Points Per Match')}
                value={form.minPointsPerMatch}
                onChange={(v) => updateField('minPointsPerMatch', v)}
                testId="input-min-points"
              />
              <NumberField
                label={t('Max Points Per Match')}
                value={form.maxPointsPerMatch}
                onChange={(v) => updateField('maxPointsPerMatch', v)}
                testId="input-max-points"
              />
            </div>
          </section>

          {/* Currency Rate */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-currency-rate">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="DollarSign" size={18} className="me-2 text-primary" />
              {t('Currency Rate')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label={`${t('Point to Currency Rate')}${currencyCode ? ` (${currencyCode})` : ''}`}
                value={form.pointToCurrencyRate}
                onChange={(v) => updateField('pointToCurrencyRate', v)}
                testId="input-currency-rate"
                step="0.01"
              />
            </div>
          </section>

          {/* Effective Date */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-effective-date">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Calendar" size={18} className="me-2 text-primary" />
              {t('Effective Date')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                id="input-effective-from"
                label={t('Effective From')}
                required
                value={form.effectiveFrom}
                onChange={(v) => updateField('effectiveFrom', v)}
                data-testid="input-effective-from"
              />
              <DateField
                id="input-effective-to"
                label={`${t('Effective To')} (${t('Optional')})`}
                value={form.effectiveTo ?? ''}
                min={form.effectiveFrom || undefined}
                onChange={(v) => updateField('effectiveTo', v || undefined)}
                data-testid="input-effective-to"
              />
            </div>
          </section>

          {/* Preview Calculator.
              Every control here had a bare <label> with no htmlFor and a control with no id, so the
              six-field calculator reached a screen reader as six unnamed widgets. The three selects
              stay NATIVE (the E2E suite drives them with selectOption); they just get wired labels. */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-preview-calculator">
            <h3 className="text-lg font-semibold text-foreground mb-1 flex items-center">
              <Icon name="Zap" size={18} className="me-2 text-primary" />
              {t('Preview Calculator')}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">{t('Estimates a referee match compensation using the active rule — all parameters on this page are reflected below (base, format, type, time bonuses, performance, caps, and currency).')}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
              <div>
                <label htmlFor="preview-role" className={labelClasses}>{t('Role')}</label>
                <select
                  id="preview-role"
                  value={previewRole}
                  onChange={(e) => setPreviewRole(e.target.value === '' ? '' : (Number(e.target.value) as MatchRole))}
                  className={inputClasses}
                  data-testid="preview-role"
                >
                  <option value="">{t('Select a role')}</option>
                  <option value={MatchRole.HeadReferee}>{t('Head Referee')}</option>
                  <option value={MatchRole.AssistantReferee}>{t('Assistant Referee')}</option>
                  <option value={MatchRole.FourthOfficial}>{t('Fourth Official')}</option>
                  <option value={MatchRole.Commissioner}>{t('Commissioner')}</option>
                </select>
              </div>
              <div>
                <label htmlFor="preview-format" className={labelClasses}>{t('Match Format')}</label>
                <select
                  id="preview-format"
                  value={previewFormat}
                  onChange={(e) => setPreviewFormat(e.target.value === '' ? '' : (Number(e.target.value) as MatchFormat))}
                  className={inputClasses}
                  data-testid="preview-format"
                >
                  <option value="">{t('Select a format')}</option>
                  <option value={MatchFormat.FiveAside}>{t('5 aside')}</option>
                  <option value={MatchFormat.SevenAside}>{t('7 aside')}</option>
                  <option value={MatchFormat.NineAside}>{t('9 aside')}</option>
                  <option value={MatchFormat.ElevenAside}>{t('11 aside')}</option>
                </select>
              </div>
              <div>
                <label htmlFor="preview-type" className={labelClasses}>{t('Match Type')}</label>
                <select
                  id="preview-type"
                  value={previewType}
                  onChange={(e) => setPreviewType(e.target.value === '' ? '' : (Number(e.target.value) as MatchType))}
                  className={inputClasses}
                  data-testid="preview-type"
                >
                  <option value="">{t('Select a type')}</option>
                  <option value={MatchType.Indoor}>{t('Indoor')}</option>
                  <option value={MatchType.Outdoor}>{t('Outdoor')}</option>
                </select>
              </div>
              <div>
                <DateField
                  id="preview-date"
                  label={t('Date')}
                  value={previewDate}
                  onChange={setPreviewDate}
                  data-testid="preview-date"
                />
              </div>
              <div>
                <TimeField
                  id="preview-hour"
                  label={t('Time')}
                  value={previewHour}
                  onChange={setPreviewHour}
                  data-testid="preview-hour"
                />
              </div>
              <div>
                <label htmlFor="preview-referee" className={labelClasses}>
                  {t('Referee')} <span className="text-muted-foreground text-xs font-normal">({t('Optional')})</span>
                </label>
                <select
                  id="preview-referee"
                  value={previewRefereeProfileId}
                  onChange={(e) => setPreviewRefereeProfileId(e.target.value)}
                  className={inputClasses}
                  data-testid="preview-referee"
                >
                  <option value="">{t('No specific referee')}</option>
                  {(refereesList?.items ?? []).map((r: any) => (
                    <option key={r.id} value={String(r.id)}>{r.name}{typeof r.rating === 'number' ? ` · ${r.rating.toFixed(1)}★` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            {previewLoading && (
              <div className="py-4">
                <LoadingState size="sm" title={t('Loading data...')} />
              </div>
            )}
            {previewResult && !previewLoading && (
              <div className="space-y-3" data-testid="preview-result">
                {/* `from-primary/10 to-blue-50` leaned on the exact primary hue, which each tenant
                    overrides at runtime — a green primary produced a green-to-baby-blue wash. */}
                <div className="bg-muted/50 border border-border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('Estimated Points')}</p>
                    <p className="text-3xl font-bold text-primary">{previewResult.estimatedPoints}</p>
                  </div>
                  <div className="text-end">
                    <p className="text-sm text-muted-foreground">{t('Currency Equivalent')}</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">{formatCurrency(previewResult.estimatedCurrencyAmount)}</p>
                  </div>
                </div>
                <div className="bg-muted/40 rounded-lg p-4">
                  <p className="text-sm font-medium text-foreground mb-2">{t('Full Breakdown')}</p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <span className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded">{t('Base')}: {previewResult.baseRolePoints}</span>
                    <span className="text-muted-foreground/60">+</span>
                    <span className="bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 px-2 py-1 rounded">{t('Format')}: {previewResult.formatPoints}</span>
                    <span className="text-muted-foreground/60">+</span>
                    <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-1 rounded">{t('Type')}: {previewResult.typeBonus}</span>
                    <span className="text-muted-foreground/60">+</span>
                    <span className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded">{t('Time')}: {previewResult.timeBonus}</span>
                    <span className="text-muted-foreground/60">+</span>
                    <span className="bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300 px-2 py-1 rounded">{t('Performance')}: {previewResult.performanceBonus}</span>
                    <span className="text-muted-foreground/60">=</span>
                    <span className="bg-muted text-foreground px-2 py-1 rounded">{t('Subtotal')}: {previewResult.rawSubtotal}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      <Icon name="Shield" size={12} className="inline me-1 text-muted-foreground/70" />
                      {t('Caps')}: {previewResult.minPointsPerMatch}–{previewResult.maxPointsPerMatch}
                    </span>
                    <span>
                      <Icon name="DollarSign" size={12} className="inline me-1 text-muted-foreground/70" />
                      {t('Rate')}: {previewResult.pointToCurrencyRate}{currencyCode ? `/${currencyCode}` : ''}
                    </span>
                    <span>
                      <Icon name={previewResult.isNonWorkingDay ? 'Calendar' : 'Sun'} size={12} className="inline me-1 text-muted-foreground/70" />
                      {previewResult.isNonWorkingDay ? t('Weekend / holiday') : t('Working day')}
                    </span>
                    {previewResult.rawSubtotal !== previewResult.estimatedPoints && (
                      <span className="text-amber-700 dark:text-amber-400">
                        <Icon name="AlertTriangle" size={12} className="inline me-1" />
                        {t('Adjusted by caps')}: {previewResult.rawSubtotal} → {previewResult.estimatedPoints}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

            {/* Save Button + Status. Was a hand-rolled px-6 py-3 button, visibly taller than every
                other CTA in the module; ui/Button's default size is the module's standard. */}
            <div className="flex flex-wrap items-center gap-4 pt-2 pb-4">
              <Button
                onClick={handleSave}
                disabled={saving}
                loading={saving}
                iconName={saving ? null : 'Save'}
                iconSize={18}
                data-testid="save-rule-btn"
              >
                {saving ? t('Saving...') : (activeRule ? t('Update Rule') : t('Create Rule'))}
              </Button>
              {saveSuccess && (
                <span role="status" className="inline-flex items-center text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800">
                  <Icon name="CheckCircle" size={16} className="me-2" />
                  {t('Rule saved successfully')}
                </span>
              )}
              {saveError && (
                <span role="alert" className="inline-flex items-center text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
                  <Icon name="AlertCircle" size={16} className="me-2" />
                  {saveError}
                </span>
              )}
            </div>

          {/* Match Durations — its own company setting with its own Save, so it sits after the rule's
              Save row rather than inside the rule form. Same section markup as the rule sections above. */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="section-match-durations">
            <h3 className="text-lg font-semibold text-foreground mb-1 flex items-center">
              <Icon name="Timer" size={18} className="me-2 text-primary" />
              {t('Match Durations')}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">{t('How long each match format lasts, in minutes. Referee shifts are created with this length. Changes apply only to matches created or edited after saving; a match can also set its own duration.')}</p>
            {durationsLoadError && (
              <div
                role="alert"
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 flex items-center gap-3"
                data-testid="match-durations-load-error"
              >
                <Icon name="AlertCircle" size={16} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-700 dark:text-red-300">{durationsLoadError}</span>
                <Button variant="outline" size="sm" onClick={loadDurations} className="ms-auto" data-testid="match-durations-retry-btn">
                  {t('Try Again')}
                </Button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <NumberField
                label={t('5-aside (minutes)')}
                value={durations.fiveAsideMinutes}
                onChange={(v) => updateDuration('fiveAsideMinutes', v)}
                testId="input-duration-five-aside"
                min={MATCH_DURATION_MIN_MINUTES}
              />
              <NumberField
                label={t('7-aside (minutes)')}
                value={durations.sevenAsideMinutes}
                onChange={(v) => updateDuration('sevenAsideMinutes', v)}
                testId="input-duration-seven-aside"
                min={MATCH_DURATION_MIN_MINUTES}
              />
              <NumberField
                label={t('9-aside (minutes)')}
                value={durations.nineAsideMinutes}
                onChange={(v) => updateDuration('nineAsideMinutes', v)}
                testId="input-duration-nine-aside"
                min={MATCH_DURATION_MIN_MINUTES}
              />
              <NumberField
                label={t('11-aside (minutes)')}
                value={durations.elevenAsideMinutes}
                onChange={(v) => updateDuration('elevenAsideMinutes', v)}
                testId="input-duration-eleven-aside"
                min={MATCH_DURATION_MIN_MINUTES}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4">
              <Button
                onClick={handleSaveDurations}
                disabled={durationsSaving}
                loading={durationsSaving}
                iconName={durationsSaving ? null : 'Save'}
                iconSize={18}
                data-testid="save-durations-btn"
              >
                {durationsSaving ? t('Saving...') : t('Save Durations')}
              </Button>
              {durationsSuccess && (
                <span role="status" className="inline-flex items-center text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800" data-testid="match-durations-saved">
                  <Icon name="CheckCircle" size={16} className="me-2" />
                  {t('Match durations saved')}
                </span>
              )}
              {durationsError && (
                <span role="alert" className="inline-flex items-center text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800" data-testid="match-durations-error">
                  <Icon name="AlertCircle" size={16} className="me-2" />
                  {durationsError}
                </span>
              )}
            </div>
          </section>
          </div>
        )}
      </PageLayout>
    </div>
  );
};

export default WorkPointSettings;
